/**
 * Unica fonte di verità per dominio, brand e normalizzazione delle stringhe SEO.
 * Nessun altro modulo deve ricostruire URL assoluti o troncare titoli a mano.
 */

export const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://cookly.studio").replace(/\/+$/, "");
export const SITE_NAME = "Cookly";
export const SITE_LOCALE = "it_IT";
export const SITE_LANGUAGE = "it-IT";

export const OG_IMAGE_WIDTH = 1200;
export const OG_IMAGE_HEIGHT = 630;

/** Immagine di condivisione di default (hero della home, ritagliata 1200x630). */
export const DEFAULT_OG_IMAGE =
  "https://images.unsplash.com/photo-1556911220-bff31c812dba?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&h=630&fit=crop&auto=format";

export const TITLE_MAX = 60;
export const DESCRIPTION_MAX = 155;

const TITLE_SUFFIX = ` — ${SITE_NAME}`;

/** CDN che accettano parametri di resize sulla query string. */
const RESIZABLE_IMAGE_HOSTS = ["images.unsplash.com", "images.pexels.com"];

/** Parole funzione italiane: se restano appese dopo un troncamento vanno rimosse. */
const DANGLING_WORDS = new Set([
  "a", "ad", "agli", "ai", "al", "alla", "alle", "allo", "che", "chi", "col", "come", "con", "cosa",
  "da", "dai", "dal", "dalla", "dalle", "dei", "del", "della", "delle", "dello", "di", "dopo", "e",
  "ed", "fra", "gli", "i", "il", "in", "la", "le", "lo", "ma", "nei", "nel", "nella", "nelle", "non",
  "o", "od", "per", "più", "prima", "quando", "se", "senza", "su", "sui", "sul", "sulla", "sulle",
  "tra", "un", "una", "uno",
]);

const ITALIAN_MONTHS = {
  gennaio: 1,
  febbraio: 2,
  marzo: 3,
  aprile: 4,
  maggio: 5,
  giugno: 6,
  luglio: 7,
  agosto: 8,
  settembre: 9,
  ottobre: 10,
  novembre: 11,
  dicembre: 12,
};

function collapseWhitespace(value) {
  return typeof value === "string" ? value.replace(/\s+/g, " ").trim() : "";
}

function pad2(value) {
  return String(value).padStart(2, "0");
}

/** Rimuove punteggiatura e parole funzione lasciate appese da un troncamento. */
function trimDangling(text) {
  let result = text.replace(/[\s\u2013\u2014,;:.!?/-]+$/u, "");
  let previous = null;
  while (result && result !== previous) {
    previous = result;
    const lastSpace = result.lastIndexOf(" ");
    if (lastSpace < 0) break;
    const lastWord = result.slice(lastSpace + 1).toLowerCase();
    if (DANGLING_WORDS.has(lastWord) || /^\d+$/.test(lastWord)) {
      result = result.slice(0, lastSpace).replace(/[\s\u2013\u2014,;:.!?/-]+$/u, "");
    }
  }
  return result;
}

/** Tronca su confine di parola senza superare `max` caratteri. */
function truncateAtWord(text, max) {
  if (text.length <= max) return text;
  const window = text.slice(0, max + 1);
  const lastSpace = window.lastIndexOf(" ");
  const cut = lastSpace > 0 ? window.slice(0, lastSpace) : text.slice(0, max);
  return trimDangling(cut);
}

/**
 * Converte un path relativo in URL assoluto sul dominio del sito.
 * @param {string} [path] Path relativo (o URL già assoluto, restituito invariato).
 * @returns {string} URL assoluto senza trailing slash, tranne la root.
 */
export function absoluteUrl(path = "/") {
  const raw = typeof path === "string" ? path.trim() : "";
  if (/^https?:\/\//i.test(raw)) return raw;
  const normalized = `/${raw}`.replace(/\/{2,}/g, "/");
  if (normalized === "/") return `${SITE_URL}/`;
  return SITE_URL + normalized.replace(/\/+$/, "");
}

/**
 * Normalizza l'immagine di condivisione a 1200x630 assoluti.
 * @param {string} [src] URL immagine (CDN, assoluto o relativo al sito).
 * @returns {string} URL assoluto utilizzabile in og:image; DEFAULT_OG_IMAGE se non risolvibile.
 */
export function ogImageUrl(src) {
  const candidate = collapseWhitespace(src);
  if (!candidate) return DEFAULT_OG_IMAGE;
  if (!/^https?:\/\//i.test(candidate)) return absoluteUrl(candidate);

  let url;
  try {
    url = new URL(candidate);
  } catch {
    return DEFAULT_OG_IMAGE;
  }
  if (!RESIZABLE_IMAGE_HOSTS.includes(url.hostname)) return url.toString();

  url.searchParams.set("w", String(OG_IMAGE_WIDTH));
  url.searchParams.set("h", String(OG_IMAGE_HEIGHT));
  url.searchParams.set("fit", "crop");
  url.searchParams.set("auto", "format");
  return url.toString();
}

/**
 * Aggiunge il suffisso di brand e tiene il titolo entro TITLE_MAX.
 * Il brand non viene mai troncato: si accorcia la parte specifica della pagina.
 * @param {string} title Titolo di pagina senza suffisso.
 * @returns {string}
 */
export function formatTitle(title) {
  const base = collapseWhitespace(title);
  if (!base) return SITE_NAME;
  if (base === SITE_NAME || base.endsWith(TITLE_SUFFIX)) return base;
  return `${truncateAtWord(base, TITLE_MAX - TITLE_SUFFIX.length)}${TITLE_SUFFIX}`;
}

/**
 * Tronca la description a DESCRIPTION_MAX su confine di parola.
 * @param {string} text
 * @returns {string}
 */
export function clampDescription(text) {
  const base = collapseWhitespace(text);
  if (base.length <= DESCRIPTION_MAX) return base;
  return `${truncateAtWord(base, DESCRIPTION_MAX - 1)}…`;
}

/**
 * Converte una data in ISO `YYYY-MM-DD`. Accetta Date, ISO o formato italiano
 * ("12 Dicembre 2025"). Non esiste una data di fallback: se non è parsabile è `null`.
 * @param {string|Date|null|undefined} value
 * @returns {string|null}
 */
export function toIsoDate(value) {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? null
      : `${value.getUTCFullYear()}-${pad2(value.getUTCMonth() + 1)}-${pad2(value.getUTCDate())}`;
  }
  if (typeof value !== "string") return null;

  const raw = value.trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  const italian = raw.match(/^(\d{1,2})\s+([\p{L}]+)\s+(\d{4})$/u);
  if (!italian) return null;

  const month = ITALIAN_MONTHS[italian[2].toLowerCase()];
  if (!month) return null;

  const day = Number(italian[1]);
  const year = Number(italian[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (parsed.getUTCMonth() !== month - 1 || parsed.getUTCDate() !== day) return null;

  return `${year}-${pad2(month)}-${pad2(day)}`;
}
